/**
 * The autosave engine: one queue slot per annotation, latest body wins.
 *
 * Plan: `docs/plans/2026-09-08-next-steps-annotation-write-volume.md` item 1b.
 * Spec: the companion `-implementation-spec.md` §2 (the slot state machine) and
 * §3 (this interface). The spec's table is the contract; the specs in
 * `tests/unit/utils/courses-autosave/engine.spec.js` are one case per row.
 *
 * WHERE THIS CAME FROM. `lesson-highlight-host.js` grew a per-note pending map
 * with a debounce, a flush on drawer close and a cancel on delete, after one
 * note was PATCHed 61 times in a sitting. That machinery was right and private
 * to one mixin; the Notes Hub had none of it and sent a request per keystroke.
 * This is that machinery, lifted out and keyed by `${type}:${itemId}` so the
 * Hub, the drawer and (from commit 4) both blank surfaces share one policy.
 *
 * WHAT IT IS NOT. It does not know what a note or a blank is, it does not touch
 * Vuex, and it does not seal. A surface registers a `sender` for its type and
 * the engine decides only WHEN that sender runs and with WHICH body.
 *
 * RULE 1 (spec §5). The caller has ALREADY committed to Vuex before it calls
 * `input`. Only the request is deferred. Deferring the commit would leave the
 * prop behind the field and let `RichTextEditor.syncFromProp` and `BlankBlock`'s
 * watcher revert the member's typing on the next render.
 *
 * SINGLE FLIGHT, and why it is here rather than in a later commit. An encrypted
 * PATCH carries the revision this device last read, so two overlapping PATCHes
 * on one row are already racy: the second names a revision the first has just
 * invalidated. A slot therefore has at most one request in the air, and a body
 * that arrives while one is running is parked and sent after it. That is
 * structural to the machine, not a feature bolted on top.
 *
 * RETRY (commit 7). A failed send is classified (`policy.js`) and, when it is
 * worth trying again, rescheduled with jittered backoff (`backoff.js`). The
 * backoff timer IS the wake: there is no "parked offline" state waiting on an
 * external signal, because an offline entry simply keeps backing off. A row that
 * exhausts its counted attempts is parked, and a new keystroke — or `retryNow`
 * from a lifecycle hook — gives it a fresh set.
 *
 * SEALING AND THE OUTBOX (phase 3). A sender takes an ALREADY-SEALED body: `seal`
 * runs before the entry is persisted to the durable `store`, so what is on disk
 * is never a member's words in the clear, and `pause` seals every waiting slot
 * to disk without sending. The replay of what is on disk is `drain.js`.
 */

import { delayFor, offlineDelay, retryAfterMs } from './backoff';
import { OUTCOME, classify, isRetryable } from './policy';

/** The queue key for a row. Exported: callers cancel and probe by key. */
export const keyFor = (type, itemId) => `${type}:${itemId}`;

/** Default flush bound. Sign-out and a page turn must never hang on a socket. */
export const DEFAULT_FLUSH_TIMEOUT_MS = 3000;

/**
 * How many COUNTED failures park a row — roughly five minutes of backoff. An
 * offline attempt is not counted (see `policy.js`), so a member in a tunnel is
 * never parked for being in a tunnel.
 */
export const DEFAULT_MAX_ATTEMPTS = 8;

/**
 * How many times one row may force its way through a stale-revision refusal
 * before it gives up and takes the server's version instead.
 *
 * Three, because each force costs a refetch and a resend, and a row that is
 * still losing after three has a writer on the other end who is not going to
 * stop for us. Nothing here is lost by giving up: the member's text is on the
 * screen they are typing on, and the yield only decides which version the
 * SERVER keeps.
 */
export const DEFAULT_MAX_CONFLICT_FORCES = 3;

/**
 * @param {object} [deps]
 * @param {number} [deps.debounceMs] idle wait before a send
 * @param {number|null} [deps.ceilingMs] hard cap from the FIRST input of a
 *   burst, so a continuous typist is not unsaved indefinitely. `null` disables
 *   it; the app passes `AUTOSAVE_CEILING_MS`.
 * @param {number} [deps.flushTimeoutMs]
 * @param {(fn: Function, ms: number) => any} [deps.setTimer]
 * @param {(id: any) => void} [deps.clearTimer]
 * @param {(member: number|null) => boolean} [deps.isSignedInMember] refuses a
 *   send for anyone but the member the edit was made for — on a shared phone a
 *   late send must never go out under the next member's token.
 * @param {(keys: string[]) => void} [deps.onPendingChange] the pending keys,
 *   after every change. The status module (`coursesAutosave.js`) hangs off this.
 * @param {((type: string, itemId: string, body: any, options?: { noNetwork?: boolean }) => Promise<any>)|null} [deps.seal]
 *   Turns a plaintext body into what goes on the wire, BEFORE it is persisted —
 *   the outbox holds envelopes, never a member's words in the clear. Without it
 *   the sender is handed the plaintext body and the transport seals, as it did
 *   before phase 3. `pause` passes `{ noNetwork: true }`: it must decide from
 *   what the device already knows, or not at all.
 * @param {{ put: Function, remove: Function }|null} [deps.store] the durable
 *   outbox. Absent, the engine is in-memory only.
 * @param {((event: string, detail?: object) => void)|null} [deps.onEvent] told
 *   about queue health — a retry scheduled, a row parked, a write given up on,
 *   the depth at `pause` — so the session summary (telemetry.js) can carry what
 *   has no HTTP footprint. Never awaited, and a throw inside it is swallowed.
 */
export function createAutosaveEngine({
  debounceMs = 400,
  ceilingMs = null,
  flushTimeoutMs = DEFAULT_FLUSH_TIMEOUT_MS,
  maxAttempts = DEFAULT_MAX_ATTEMPTS,
  maxConflictForces = DEFAULT_MAX_CONFLICT_FORCES,
  setTimer = (fn, ms) => setTimeout(fn, ms),
  clearTimer = id => clearTimeout(id),
  random = Math.random,
  isSignedInMember = () => true,
  onPendingChange = null,
  seal = null,
  store = null,
  onEvent = null,
} = {}) {
  const report = (event, detail) => {
    if (!onEvent) return;
    try {
      onEvent(event, detail);
    } catch (error) {
      /* Telemetry must not be able to break the queue. */
    }
  };
  // Mutable so the app instance can be created at import time and pointed at
  // the store once there is one — the same shape as `installSealedWrites` and
  // `installPlaintextCacheGuard`.
  let memberCheck = isSignedInMember;
  // Same reason: the app instance exists before there is a store to seal with.
  let sealFn = seal;
  let durable = store;
  /** key -> slot. A slot exists only while something is unsaved for that row. */
  const slots = new Map();
  /** type -> sender */
  const senders = new Map();
  /** type -> callback given the opened response row after a successful send. */
  const persistedHandlers = new Map();
  /**
   * type -> listeners told when a send failed. Several, because two parties want
   * different things from one failure: the store settles the row (a 404 is
   * final, a sealing refusal restores), while the surface on screen says
   * something about it. Each listener has one job and neither knows the other.
   */
  const errorListeners = new Map();
  /**
   * Rows the server has answered 404 for. Nothing we send revives them, so
   * further input stops here rather than repeating the failure on every
   * keystroke (the mixin's `unsavableNoteIds`, generalised).
   */
  const unsavable = new Set();
  /**
   * key -> the body the server last accepted, as JSON. Item 1d: a body equal to
   * it is not a write. Kept as a string because the comparison is a deep one and
   * a note body is a small tree — `JSON.stringify` is the cheapest deep compare
   * available here, and stable enough for it, since every body this engine sees
   * is built by the same code path.
   *
   * Outlives the slot deliberately: a slot is deleted the moment its send lands,
   * and the whole point is to recognise a body that comes back round afterwards
   * — a bold toggled and untoggled, or a word typed and deleted.
   */
  const persisted = new Map();

  // Mutable for the same reason `memberCheck` is: the app instance exists before
  // the store does.
  let pendingReporter = onPendingChange;
  const notify = () => {
    if (pendingReporter) pendingReporter([...slots.keys()]);
  };

  /** Item 1d. Never throws: a body that will not stringify is simply not a no-op. */
  const sameAsPersisted = (key, body) => {
    try {
      return persisted.has(key) && persisted.get(key) === JSON.stringify(body);
    } catch (error) {
      return false;
    }
  };
  const rememberPersisted = (key, body) => {
    try {
      persisted.set(key, JSON.stringify(body));
    } catch (error) {
      persisted.delete(key);
    }
  };

  function clearTimers(slot) {
    if (slot.idleTimer !== null) clearTimer(slot.idleTimer);
    if (slot.ceilingTimer !== null) clearTimer(slot.ceilingTimer);
    if (slot.backoffTimer !== null) clearTimer(slot.backoffTimer);
    slot.idleTimer = null;
    slot.ceilingTimer = null;
    slot.backoffTimer = null;
  }

  /**
   * Schedule another attempt. `counted` is false for "there is no network",
   * which must not spend a member's attempts.
   */
  function scheduleRetry(slot, { counted, afterMs }) {
    if (counted) slot.attempts += 1;
    if (counted && slot.attempts >= maxAttempts) {
      // Parked: the timers are gone and nothing wakes this but a new keystroke
      // or `retryNow`. It stays in `slots`, so it is still pending and still
      // holds the member's words.
      slot.parkedOut = true;
      clearTimers(slot);
      report('parked', { key: slot.key });
      notify();
      return;
    }
    report(counted ? 'retry' : 'retryUncounted', { key: slot.key });
    const wait =
      afterMs !== null && afterMs !== undefined
        ? afterMs
        : counted
          ? delayFor(slot.attempts - 1, random)
          : offlineDelay(random);
    slot.backoffTimer = setTimer(() => {
      slot.backoffTimer = null;
      void send(slot);
    }, wait);
    notify();
  }

  function armTimers(slot) {
    if (slot.idleTimer !== null) clearTimer(slot.idleTimer);
    slot.idleTimer = setTimer(() => {
      slot.idleTimer = null;
      void send(slot);
    }, debounceMs);
    // The ceiling runs from the FIRST input of a burst and is not restarted by
    // later ones — that is the whole point of it.
    if (ceilingMs !== null && slot.ceilingTimer === null) {
      slot.ceilingTimer = setTimer(() => {
        slot.ceilingTimer = null;
        void send(slot);
      }, ceilingMs);
    }
  }

  /**
   * The slot is finished with, and so is its entry on disk. Every path here is
   * final for the write — landed, a no-op, a verdict from the server, a
   * conflict out of forces, no sender — and an entry left behind would be
   * replayed by the next drain, refused the same way, and left again until the
   * thirty-day sweep. A remove for a key with no entry is a no-op, so this
   * does not have to know whether SEND or `pause` wrote one.
   */
  function drop(slot) {
    clearTimers(slot);
    slots.delete(slot.key);
    forgetDurable(slot);
    notify();
  }

  function forgetDurable(slot) {
    if (!durable) return;
    Promise.resolve(durable.remove(slot.member, slot.key)).catch(() => {});
  }

  /**
   * Run the slot's sender once. Resolves when the attempt has settled; never
   * rejects, so a caller awaiting `flush` is not derailed by one bad row.
   */
  async function send(slot) {
    // A slot removed while its timer was pending, or a second trigger landing
    // on a slot already in the air (the parked body covers that case).
    if (slots.get(slot.key) !== slot || slot.sending) return;
    clearTimers(slot);

    // Not this member's session any more. The slot is KEPT rather than dropped:
    // it holds the only copy of what they typed, and the outbox decides its
    // fate. Nothing is sent.
    if (!memberCheck(slot.member)) return;

    const sender = senders.get(slot.type);
    if (!sender) {
      drop(slot);
      return;
    }

    const body = slot.body;
    // Item 1d: the server already has exactly this. Opening a drawer, tapping
    // into the field and closing it used to send a PATCH that changed nothing.
    if (sameAsPersisted(slot.key, body)) {
      drop(slot);
      return;
    }
    slot.sending = true;
    slot.inFlight = (async () => {
      try {
        // Seal first, and persist the envelope BEFORE the request. A process
        // killed between the two has to leave the member's words somewhere, and
        // the only form they may be left in is the sealed one.
        const sealed = sealFn ? await sealFn(slot.type, slot.itemId, body) : body;
        if (durable) {
          await durable.put(slot.member, {
            key: slot.key,
            type: slot.type,
            itemId: slot.itemId,
            sealed: sealed === undefined ? null : sealed,
            meta: slot.meta,
            inFlight: true,
          });
        }
        const row = await sender(slot.itemId, sealed, slot.meta);
        if (durable) await durable.remove(slot.member, slot.key);
        rememberPersisted(slot.key, body);
        persistedHandlers.get(slot.type)?.(slot.itemId, row);
        return true;
      } catch (error) {
        slot.lastOutcome = classify(error);
        slot.lastError = error;
        // A row the server says is gone stops accepting input, not just sending
        // — the drawer's old `unsavableNoteIds`, for every surface.
        if (slot.lastOutcome === OUTCOME.DROP_UNSAVABLE) unsavable.add(slot.key);
        // Counted BEFORE the listeners, because the number is what tells them
        // which way this conflict goes: force through, or give up and take the
        // server's version. Consecutive by construction — a slot is dropped the
        // moment a send lands, so a fresh one always starts at zero.
        if (slot.lastOutcome === OUTCOME.CONFLICT) slot.conflicts += 1;
        // Told either way: the store settles the row, the screen reports it.
        (errorListeners.get(slot.type) || []).forEach(listener => {
          try {
            listener({
              key: slot.key,
              type: slot.type,
              itemId: slot.itemId,
              meta: slot.meta,
              // The body that was actually sent, which after coalescing is not
              // the one the caller passed when the slot was created. A listener
              // deciding whether a later keystroke has moved things on needs
              // this one, not `meta`.
              body,
              error,
              outcome: slot.lastOutcome,
              // Which of the two send paths this failure came from. The drain
              // builds its own failure object with `'replay'`; a slot in memory
              // is always the member working in the row right now, which is
              // what earns the right to force a conflict through.
              origin: 'live',
              // How many times in a row this row has been refused as stale.
              conflicts: slot.conflicts,
            });
          } catch (listenerError) {
            /* A reporter must not be able to break the queue. */
          }
        });
        return false;
      }
    })();

    const ok = await slot.inFlight;
    slot.sending = false;
    slot.inFlight = null;

    if (slots.get(slot.key) !== slot) return;

    if (!ok) {
      const outcome = slot.lastOutcome;
      // A newer body arrived while that one was failing; it is what the retry
      // carries, and it is also a fresh edit, so it earns fresh attempts.
      if (slot.parked !== undefined) {
        slot.body = slot.parked;
        slot.parked = undefined;
        slot.attempts = 0;
      }
      if (isRetryable(outcome)) {
        scheduleRetry(slot, {
          counted: outcome === OUTCOME.RETRY,
          afterMs: retryAfterMs(slot.lastError),
        });
        return;
      }
      // ANOTHER DEVICE WROTE FIRST, AND THIS ONE IS BEING TYPED IN. The slot is
      // KEPT rather than dropped: `forceThroughConflict` refetches the row for
      // its fresh revision and calls `retryNow(key)`, and there would be nothing
      // left to send if the slot went here. No timer is armed — the refetch is
      // the wake, and a keystroke in the meantime arms one as it always does.
      //
      // Bounded. A fourth writer landing between three refetches is not a race
      // worth chasing, so the last refusal falls through and the slot goes; the
      // listener reads the same count and takes the server's version instead.
      if (outcome === OUTCOME.CONFLICT && slot.conflicts < maxConflictForces) {
        notify();
        return;
      }
      // DROP, DROP_UNSAVABLE, REFUSE and a conflict out of forces are all final
      // for this write. The listeners have already been told; the slot goes.
      report('dropped', {
        key: slot.key,
        outcome,
        code: slot.lastError?.code ?? slot.lastError?.response?.status ?? outcome,
      });
      drop(slot);
      return;
    }

    slot.attempts = 0;
    if (slot.parked === undefined || sameAsPersisted(slot.key, slot.parked)) {
      // Nothing newer, or newer but identical to what just landed — typed and
      // undone while the request was in the air.
      drop(slot);
      return;
    }
    // A newer body arrived while that one was in the air. Debounce it rather
    // than sending straight away, so a fast typist does not chain requests
    // back to back for the whole time they are typing.
    slot.body = slot.parked;
    slot.parked = undefined;
    armTimers(slot);
    notify();
  }

  /**
   * A surface's every keystroke, after it has committed to Vuex.
   *
   * @param {string} type the sender to use
   * @param {string} itemId the row
   * @param {*} body what to send
   * @param {object} [options]
   * @param {number|null} [options.member] who this edit belongs to
   * @param {*} [options.meta] passed through to the sender untouched (the
   *   drawer's `highlightId`, for one), so the engine needs no per-type shape
   */
  function input(type, itemId, body, { member = null, meta = undefined } = {}) {
    if (!type || !itemId) return;
    const key = keyFor(type, itemId);
    if (unsavable.has(key)) return;

    let slot = slots.get(key);
    if (!slot) {
      slot = {
        key,
        type,
        itemId,
        body,
        parked: undefined,
        member,
        meta: meta ?? null,
        idleTimer: null,
        ceilingTimer: null,
        backoffTimer: null,
        sending: false,
        inFlight: null,
        /** Counted failures. Offline ones do not add to this. */
        attempts: 0,
        /**
         * Consecutive stale-revision refusals. Separate from `attempts`: a
         * conflict is not a network failure, it is another device having
         * written first, and the two are bounded differently.
         */
        conflicts: 0,
        /** Out of attempts: nothing is scheduled, but the words are still held. */
        parkedOut: false,
        lastOutcome: null,
        lastError: null,
      };
      slots.set(key, slot);
    }
    slot.member = member;
    // NOT updated after the slot exists. `meta` is where a caller records the
    // state BEFORE the burst — a blank's previous answer, for the
    // sealing-refusal restore — and a later keystroke's idea of "previous" is a
    // value the member has already typed. The body it should be compared
    // against travels separately, on the failure itself.

    if (slot.sending) {
      // Single flight: hold the newest body until the request in the air lands.
      slot.parked = body;
      return;
    }
    // A fresh edit on a row that had run out of attempts starts the count again:
    // the member is still working, and what is being sent is not the thing that
    // kept failing.
    if (slot.parkedOut) {
      slot.parkedOut = false;
      slot.attempts = 0;
    }
    slot.body = body;
    armTimers(slot);
    notify();
  }

  /** Wait for one slot to have been attempted, including a parked follow-up. */
  async function flushSlot(slot) {
    if (!slot) return;
    if (slot.sending) await slot.inFlight;
    const current = slots.get(slot.key);
    if (current) await send(current);
  }

  /**
   * Send now. With no key, every slot. Resolves when the sends have been
   * ATTEMPTED — bounded, because sign-out and a page turn must not hang on a
   * dead network — so a caller must not read it as "saved".
   *
   * `timeoutMs` overrides the engine's bound for this one call (sign-out passes
   * `SIGN_OUT_FLUSH_TIMEOUT_MS`, and says why there). Afterwards `pendingKeys()`
   * says what is still unsent.
   *
   * @param {string} [key]
   * @param {{ timeoutMs?: number }} [options]
   * @returns {Promise<void>}
   */
  function flush(key, { timeoutMs = flushTimeoutMs } = {}) {
    const targets = key ? [slots.get(key)] : [...slots.values()];
    const attempts = Promise.all(targets.map(slot => flushSlot(slot))).then(() => {});
    let bound;
    const bounded = new Promise(resolve => {
      bound = setTimer(resolve, timeoutMs);
    });
    return Promise.race([attempts.then(() => clearTimer(bound)), bounded]);
  }

  /**
   * Forget a row's pending edit. Every delete path calls this — and the entry
   * on disk goes with the slot, or the drain would replay an edit to a row that
   * has since been deleted, be refused 404, and settle it then; the delete is
   * the moment that is known here. A key with no slot may still have an entry
   * from an earlier session, but that entry's member is not known here; the
   * drain's own classification settles it on the 404.
   */
  function cancel(key) {
    persisted.delete(key);
    const slot = slots.get(key);
    if (!slot) return;
    clearTimers(slot);
    slots.delete(slot.key);
    forgetDurable(slot);
    notify();
  }

  /**
   * Forget every row's pending edit, and everything else that is about a ROW
   * — the unsavable set and the last-accepted bodies — but keep the wiring:
   * senders, listeners, the member check and the durable store all belong to
   * the process, not to a member.
   *
   * Sign-out calls this after it has swept the member's outbox. It used to
   * sweep the outbox and leave the slots: a row still debouncing, or backing
   * off from a failure, kept its timer, and when that fired `send` found no
   * signed-in member to compare against (`memberCheck` allows a null one) and
   * ran — writing the signed-out member's words back to the outbox that had
   * just been emptied, and putting them on the wire under whatever token was
   * left. `reset` is not the right call for this: it clears the listeners
   * `installAutosave` registered, and the next member's failures would then
   * reach nobody.
   */
  function cancelAll() {
    slots.forEach(clearTimers);
    slots.clear();
    unsavable.clear();
    persisted.clear();
    notify();
  }

  return {
    keyFor,
    input,
    flush,
    cancel,
    cancelAll,
    /**
     * Everything unsent goes to disk, and NOTHING is sent.
     *
     * iOS gives an app about five seconds after it goes to the background before
     * it freezes, and it can only kill a frozen app — so this is the last code
     * that runs before the process may cease to exist. A network request would
     * be a race against that; an IndexedDB-free Preferences write is
     * milliseconds and wins it. Starting a request here and hoping was the
     * whole reason the camera-swap case lost work (plan §3.3).
     *
     * Timers are frozen with the webview and are not cleared: whatever was
     * debouncing carries on debouncing when the app comes back.
     */
    pause: async () => {
      // Read synchronously, before the first await: App.vue emits the summary
      // right after calling this, without waiting on the Preferences writes.
      report('pause', { depth: slots.size });
      if (!durable) return;
      const waiting = [...slots.values()].filter(slot => !slot.sending);
      for (const slot of waiting) {
        // A quick resume can start sending a row while this loop is still
        // sealing the one before it — `waiting` was read once, at the top. A
        // row that is sending owns its own entry now (SEND writes `inFlight:
        // true` and removes it on success); writing `inFlight: false` for it
        // here, possibly AFTER that removal, would put a landed entry back on
        // disk to be replayed and refused as stale on the next launch. Checked
        // again after the seal below, which is the await the send can land in.
        if (slot.sending || slots.get(slot.key) !== slot) continue;
        try {
          // `noNetwork`: sealing may need to ask the server whether this member
          // is enrolled, and a request here is a race against the freeze. The
          // hook answers from what the device already knows, or refuses.
          const sealed = sealFn
            ? await sealFn(slot.type, slot.itemId, slot.body, { noNetwork: true })
            : slot.body;
          if (slot.sending || slots.get(slot.key) !== slot) continue;
          await durable.put(slot.member, {
            key: slot.key,
            type: slot.type,
            itemId: slot.itemId,
            sealed: sealed === undefined ? null : sealed,
            meta: slot.meta,
            inFlight: false,
          });
        } catch (error) {
          // A row that cannot be sealed cannot be persisted. It stays in the
          // slot and is sent on resume; only a kill in between loses it, and
          // the count is how we learn whether that ever happens in the field.
          // Nothing else in the loop is affected by it.
          report('pausePersistFailed', {
            key: slot.key,
            code: error?.code ?? error?.response?.status ?? 'unknown',
          });
        }
      }
    },
    /** Attach the durable store and the sealing step once the app has both. */
    setDurableStore: (nextStore, nextSeal) => {
      durable = nextStore ?? null;
      sealFn = typeof nextSeal === 'function' ? nextSeal : null;
    },
    /**
     * Try a waiting row now, with its attempts reset — for a lifecycle hook
     * that has reason to believe the world changed: coming back to the
     * foreground, or the network returning. Rows in flight are left alone.
     *
     * With no key, every row, and it is not needed for correctness: a
     * backing-off row has a timer and will try itself. That form only makes it
     * sooner, and un-parks a row that ran out.
     *
     * WITH a key it IS needed: a row refused as stale keeps its slot and arms no
     * timer, so `forceThroughConflict` calling this after its refetch is the
     * only thing that sends it. The unkeyed form would send every other parked
     * row at the same time, which is not what a conflict on one row asks for.
     *
     * @param {string} [key]
     */
    retryNow: key => {
      const targets = key ? [slots.get(key)] : [...slots.values()];
      targets.forEach(slot => {
        if (!slot || slot.sending) return;
        slot.attempts = 0;
        slot.parkedOut = false;
        void send(slot);
      });
    },
    /** Rows out of attempts — what the one banner in §6.5 is counted from. */
    parkedKeys: () => [...slots.values()].filter(slot => slot.parkedOut).map(slot => slot.key),
    /** @param {string} type @param {Function} sender */
    registerSender: (type, sender) => senders.set(type, sender),
    /** @param {string} type @param {Function} fn given (itemId, row) after a send */
    onPersisted: (type, fn) => persistedHandlers.set(type, fn),
    /**
     * Subscribe to failures for a type. Returns the unsubscribe, which a
     * component must call on unmount — otherwise a stale page keeps reporting
     * failures for rows the member is no longer looking at.
     *
     * @param {string} type
     * @param {(failure: { key, type, itemId, meta, error }) => void} listener
     * @returns {() => void}
     */
    onSendError: (type, listener) => {
      const list = errorListeners.get(type) || [];
      list.push(listener);
      errorListeners.set(type, list);
      return () => {
        const current = errorListeners.get(type) || [];
        const at = current.indexOf(listener);
        if (at !== -1) current.splice(at, 1);
      };
    },
    /** Whether a row has an edit not yet known to be on the server. */
    hasPending: key => slots.has(key),
    /**
     * Every row with an edit not yet known to be on the server, in flight or
     * waiting. What sign-out counts after its flush, to say how many edits a
     * "sign out anyway" would lose.
     */
    pendingKeys: () => [...slots.keys()],
    /**
     * The 404 rule: stop trying, and stop accepting input, for this row — and
     * let go of what it still holds. A slot can be here when this is called:
     * `forceThroughConflict` marks a row whose conflict refetch answered 404,
     * and the engine KEEPS a slot through a conflict (§6.5) waiting for the
     * refetch's `retryNow`. Left in place, the next `flush` — a page turn, a
     * blur, sign-out — resent it, to be refused stale again, up to the force
     * cap, and then yielded; the words are the other device's deletion either
     * way. The entry on disk goes with it.
     */
    markUnsavable: key => {
      unsavable.add(key);
      const slot = slots.get(key);
      if (slot) drop(slot);
    },
    /**
     * Point the engine at the signed-in member. See `installAutosave`.
     * @param {(member: number|null) => boolean} fn
     */
    setMemberCheck: fn => {
      memberCheck = typeof fn === 'function' ? fn : memberCheck;
    },
    /**
     * Where the pending keys go after every change — the store, in the app.
     * `null` detaches, which `installAutosave`'s teardown does.
     * @param {((keys: string[]) => void)|null} fn
     */
    setPendingReporter: fn => {
      pendingReporter = typeof fn === 'function' ? fn : null;
    },
    /** Test and teardown seam: drop everything without sending. */
    reset: () => {
      slots.forEach(clearTimers);
      slots.clear();
      unsavable.clear();
      persisted.clear();
      errorListeners.clear();
      notify();
    },
  };
}
